import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { enqueueSnackbar } from "notistack";

import MediaSettings from "./UserMediaSetting";
import NotificationSettings from "./NotificationSetting";
import SecuritySettings from "./SecuritySettings";
import DangerZone from "./DangerZone";
import { NewStyledNetflixButton } from "../../Dashboard/Talent/Schedules/EditDeleteAvailability";

import { useAuth } from "../../../hooks";
import { useUpdateProfile } from "../../../services/api/talent.api";
import { useGetCategories } from "../../../services/api/card.api";

/* ---------- Netflix-like tokens ---------- */
const NF = {
  bg: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.78)",
  border: "rgba(255,255,255,0.14)",
  borderStrong: "rgba(255,255,255,0.22)",
  red: "#E50914",
  glow: "radial-gradient(900px 420px at 10% -10%, rgba(229,9,20,0.10), transparent 55%), radial-gradient(600px 300px at 110% 0%, rgba(255,255,255,0.05), transparent 60%)",
};
const lexend = { fontFamily: "'Lexend', sans-serif" };

/* ---------- File-upload helper ---------- */
const uploadFile = async (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch("https://api.taasnet.com/api/v1/file-upload", {
    method: "POST",
    body: fd,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access_token") ?? ""}`,
    },
  });
  if (!r.ok) throw new Error("Upload failed");
  return (await r.json()).data.url as string;
};

type PrefKeys = "email" | "bookings" | "reminders";

interface InputData {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  category: string;
  location: string;
  bio: string;
  profile_image: string | File;
}

/* ---------- Dark theme input wrapper ---------- */
const DarkTextField = (props: any) => (
  <TextField
    {...props}
    sx={{
      "& .MuiInputBase-root": {
        backgroundColor: "rgba(255,255,255,0.06)",
        color: NF.text,
        borderRadius: 2,
        "& fieldset": {
          borderColor: NF.border,
        },
        "&:hover fieldset": {
          borderColor: "rgba(255,255,255,0.2)",
        },
        "&.Mui-focused fieldset": {
          borderColor: NF.red,
        },
      },
      "& .MuiInputLabel-root": {
        color: NF.muted,
      },
      "& .MuiFormHelperText-root": {
        color: NF.muted,
      },
      ...props.sx,
    }}
  />
);

const UserSettingsPage: React.FC = () => {
  const { userInfo, setUser } = useAuth();
  const { update } = useUpdateProfile({ key: ["updateProfile"] });

  const { data: categoryData } = useGetCategories({ key: ["categories"] });

  const categoryOptions =
    categoryData?.data?.map((category: any) => ({
      label: category.name,
      value: category.name,
    })) || [];

  /* ------ Prefs (NotificationSettings) ------ */
  const initialPrefs = useMemo<Record<PrefKeys, boolean>>(
    () => ({
      email:
        (userInfo as any)?.contact_preferences?.email ??
        (userInfo as any)?.contact_peference?.email ??
        true,
      bookings:
        (userInfo as any)?.contact_preferences?.bookings ??
        (userInfo as any)?.contact_peference?.bookings ??
        true,
      reminders:
        (userInfo as any)?.contact_preferences?.reminders ??
        (userInfo as any)?.contact_peference?.reminders ??
        true,
    }),
    [userInfo]
  );
  const [prefs, setPrefs] = useState<Record<PrefKeys, boolean>>(initialPrefs);
  const handlePrefChange = (k: PrefKeys, v: boolean) =>
    setPrefs((p) => ({ ...p, [k]: v }));

  /* ------ MUI helpers ------ */
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  /* ------ Formik (InputData) ------ */
  const formik = useFormik<InputData>({
    enableReinitialize: true,
    initialValues: {
      first_name: userInfo?.first_name ?? "",
      last_name: userInfo?.last_name ?? "",
      email: userInfo?.email ?? "",
      username: userInfo?.username ?? "",
      location: userInfo?.location ?? "",
      category:
        userInfo?.categories && userInfo.categories.length > 0
          ? userInfo.categories[0]
          : "",
      bio: userInfo?.bio ?? "",
      profile_image: userInfo?.profile_image ?? "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().min(2, "Too short").required("Required"),
      last_name: Yup.string().min(2, "Too short").required("Required"),
      email: Yup.string().email("Invalid email").required("Required"),
      username: Yup.string().required("Required"),
      category: Yup.string().optional(),
      location: Yup.string().optional(),
      bio: Yup.string().optional(),
    }),
    onSubmit: async (vals) => {
      try {
        let profileUrl = vals.profile_image as string;
        if (vals.profile_image instanceof File) {
          profileUrl = await uploadFile(vals.profile_image);
        }

        const payload = {
          ...vals,
          profile_image: profileUrl,
          categories: [vals.category],
          contact_preferences: prefs,
        };

        await update(payload, {
          onSuccess: (data) => {
            setUser(data?.data);
            enqueueSnackbar("User profile updated!", { variant: "success" });
          },
          onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || "Update failed", {
              variant: "error",
            });
          },
        });
      } catch (err: any) {
        console.error("Update failed:", err);
        enqueueSnackbar(err?.message ?? "Update failed", { variant: "error" });
      }
    },
  });

  const canSave = formik.dirty && formik.isValid && !formik.isSubmitting;

  /* ---------- JSX ---------- */
  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: 4,
        ...lexend,
        backgroundColor: "#0b0b0b",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Paper
        sx={{
          p: { xs: 2, sm: 3, md: 5 },
          borderRadius: 3,
          color: NF.text,
          background: `${NF.glow}, ${NF.bg}`,
          border: `1px solid ${NF.border}`,
          boxShadow: "0 24px 60px rgba(0,0,0,.55)",
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          divider={
            <Divider
              flexItem
              orientation={mdUp ? "vertical" : "horizontal"}
              sx={{ borderColor: NF.border }}
            />
          }
          spacing={{ xs: 3, md: 4 }}
        >
          {/* LEFT column */}
          <Box component="form" flex={1} onSubmit={formik.handleSubmit}>
            <Typography
              sx={{
                fontWeight: 900,
                letterSpacing: ".2px",
                textShadow: "0 1px 0 rgba(0,0,0,.35)",
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                mb: { xs: 2, md: 3 },
                color: NF.text,
              }}
            >
              User Settings
            </Typography>

            {/* Media */}
            <MediaSettings
              profileUrl={
                typeof formik.values.profile_image === "string"
                  ? formik.values.profile_image
                  : undefined
              }
              onChange={(f) => {
                formik.setFieldValue("profile_image", f);
              }}
            />

            {/* Basic text fields */}
            <Stack spacing={3} mt={4}>
              <DarkTextField
                label="First Name"
                name="first_name"
                value={formik.values.first_name}
                onChange={formik.handleChange}
                error={
                  formik.touched.first_name && Boolean(formik.errors.first_name)
                }
                helperText={
                  formik.touched.first_name && formik.errors.first_name
                }
                fullWidth
              />

              <DarkTextField
                label="Last Name"
                name="last_name"
                value={formik.values.last_name}
                onChange={formik.handleChange}
                error={
                  formik.touched.last_name && Boolean(formik.errors.last_name)
                }
                helperText={formik.touched.last_name && formik.errors.last_name}
                fullWidth
              />

              <DarkTextField
                label="Username (nickname)"
                name="username"
                value={formik.values.username}
                onChange={formik.handleChange}
                error={
                  formik.touched.username && Boolean(formik.errors.username)
                }
                helperText={formik.touched.username && formik.errors.username}
                fullWidth
              />

              <DarkTextField
                label="Email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
              />

              <Autocomplete
                fullWidth
                options={categoryOptions}
                getOptionLabel={(option) => option.label}
                value={
                  categoryOptions.find(
                    (opt) => opt.value === formik.values.category
                  ) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue("category", newValue?.value ?? "");
                }}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    backgroundColor: "rgba(255,255,255,0.06)",
                    color: NF.text,
                    "& fieldset": {
                      borderColor: NF.border,
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: NF.red,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: NF.muted,
                  },
                }}
                renderInput={(params) => (
                  <DarkTextField
                    {...params}
                    label="Select Category"
                    placeholder="Search Category..."
                    error={
                      formik.touched.category && Boolean(formik.errors.category)
                    }
                    helperText={
                      formik.touched.category && formik.errors.category
                    }
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      backgroundColor: NF.bg,
                      color: NF.text,
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    {option.label}
                  </Box>
                )}
                PaperComponent={({ children, ...other }) => (
                  <Paper
                    {...other}
                    sx={{
                      backgroundColor: NF.bg,
                      border: `1px solid ${NF.border}`,
                      boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
                    }}
                  >
                    {children}
                  </Paper>
                )}
              />

              <DarkTextField
                label="Location"
                name="location"
                value={formik.values.location}
                onChange={formik.handleChange}
                error={
                  formik.touched.location && Boolean(formik.errors.location)
                }
                helperText={formik.touched.location && formik.errors.location}
                fullWidth
              />

              <DarkTextField
                label="Bio"
                name="bio"
                multiline
                rows={3}
                value={formik.values.bio}
                onChange={formik.handleChange}
                error={formik.touched.bio && Boolean(formik.errors.bio)}
                helperText={formik.touched.bio && formik.errors.bio}
                fullWidth
              />
            </Stack>

            {/* Save */}
            <Box mt={4} textAlign={{ xs: "center", md: "right" }}>
              <NewStyledNetflixButton
                type="submit"
                variant="contained"
                disabled={!canSave}
                startIcon={
                  formik.isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{
                  textTransform: "none",
                  px: 4,
                  py: 1.3,
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              >
                {formik.isSubmitting ? "Saving…" : "Save Changes"}
              </NewStyledNetflixButton>
            </Box>
          </Box>

          {/* RIGHT column */}
          <Box flex={1}>
            {/* Notification Preferences */}
            <NotificationSettings prefs={prefs} onChange={handlePrefChange} />

            <Divider sx={{ my: 4, borderColor: NF.border }} />

            {/* Security (password change) */}
            <SecuritySettings />

            <Divider sx={{ my: 4, borderColor: NF.border }} />

            {/* Danger Zone */}
            <DangerZone
              onDelete={() => console.log("DELETE")}
              onDeactivate={() => console.log("DEACTIVATE")}
            />
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default UserSettingsPage;
